import { UIElement } from '../ui-element';
import { Labels } from '../../global/labels';
import { UIBadge } from '../badge/ui-badge';
import { BrandColors } from '../../global/helpers-marketing';
import { UIIcon } from '../icon/ui-icon';
import { browser } from '../../global/browser';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import type {
    AttributeValue,
    ElementChildren,
    ElementConfig,
} from '../ui-element.type';
import type { TranslationLabels } from '../../types/labels.type';
import type {
    UINavItemType,
    UINavItemTypes,
    UINavItemLayout,
    UINavItemLayouts,
    UINavItemPosition,
    UINavItemPositions,
} from './ui-navitem.type';
import type { NullOr, UndefinedOr } from '../../global/utils.type';
import type { UINav } from './ui-nav';
import styles from './ui-navitem.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UINavItem
 * @element ui-navitem
 * @classdesc Represents a class for <code>ui-navitem</code> element
 * @fires event:remove-shortcut-item
 * @property {string} label {@attr label}
 * @property {string} labelId {@attr label-id}
 * @property {string} [labelBack] {@attr label-back} Label to set back button label
 * inside ui-navlist.
 * @property {string} [labelHint] {@attr label-hint} Label hint inside primary-item.
 * @property {string} [controls] {@attr controls} ID of related menu block.
 * @property {string} [href] {@attr href} ID of related menu block.
 * @property {string} [glyph] {@attr glyph} Glyph for item as a primary icon or primary item.
 * @property {string} [glyphColor] {@attr glyph-color} Glyph color for item as a primary icon
 * or primary item.
 * @property {string} [item] {@attr item} ID for item as link.
 * @property {string} [badge] {@attr badge} Badge for the menu usually using 1 symbol '!',
 * please also do not use large badge in the menu. The large badge won't fit.
 * @property {("fluid")} [layout] {@attr layout} Layout for navitem view.
 * @property {INavItemType} [type] {@attr type} Type for nav item:
 *   {@desc primary-item: layout for first level nav item}
 *   {@desc secondary-item: layout for second level nav item}
 *   {@desc auxiliary-item: layout for auxiliary level nav item}
 *   {@desc primary-icon: layout for nav item with icon}
 *   {@desc custom: layout for nav item with custom content}
 * @property {"before" | "after"} [navItemPosition] {@attr position} Position for custom nav item.
 *   {@desc before: place custom item before primary menu items}
 *   {@desc after: place custom item after primary menu items}
 * @property {boolean} [edit] {@attr edit} Edit enable flag for shortcuts.
 * @property {boolean} [expandable] {@attr expandable} Expandable menu, used for
 * auxiliary and mobile menus.
 * @example
 * <ui-navitem></ui-navitem>
 */
@Component({ tag: 'ui-navitem', styles })
export class UINavItem extends UIElement {
    /**
     * @returns {Record<INavItemType, string>}
     * @deprecated - Use Types
     */
    static readonly types: UINavItemTypes = {
        PRIMARY: 'primary-item',
        SECONDARY: 'secondary-item',
        AUXILIARY: 'auxiliary-item',
        PRIMARY_ICON: 'primary-icon',
        CUSTOM: 'custom',
    };

    static readonly Types = UINavItem.types;

    static readonly Layouts: UINavItemLayouts = {
        Fluid: 'fluid',
    };

    static readonly Positions: UINavItemPositions = {
        Before: 'before',
        After: 'after',
    };

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-navitem', {
            REMOVE_SHORTCUT: 'Delete shortcut',
        });
    }

    @Attribute(String) badge?: string;
    @Attribute(String) label?: string;
    @Attribute(String) labelId?: string;
    @Attribute(String) href?: string;
    @Attribute(Boolean) active?: boolean;
    @Attribute(Boolean) edit?: boolean;
    @Attribute(Boolean) expandable?: boolean;
    @Attribute(String) glyph?: string;
    @Attribute(String) labelBack?: string;
    @Attribute(String) labelHint?: string;
    @Attribute(String) type?: UINavItemType;
    @Attribute(String) layout?: UINavItemLayout;
    @Attribute({ type: String, default: UIIcon.colors.DEFAULT })
    glyphColor: string = UIIcon.colors.DEFAULT;
    @Attribute({ type: String, name: 'position' })
    navItemPosition?: UINavItemPosition;

    /** Reference to parent ui-nav element */
    nav?: NullOr<UINav>;

    static get observedAttributes(): Array<string> {
        return ['edit'];
    }

    /**
     * Checks if the item is primary
     * @returns {boolean}
     */
    isPrimaryItem(): boolean {
        return this.type === UINavItem.Types.PRIMARY;
    }

    /**
     * Checks if the item is secondary
     * @returns {boolean}
     */
    isSecondaryItem(): boolean {
        return this.type === UINavItem.Types.SECONDARY;
    }

    /**
     * Get ID of related menu block
     * @returns {string}
     */
    getControlId(): string {
        return this.getAttribute('controls') || '';
    }

    /**
     * Get ID of related menu block
     * @returns {HTMLElement | null}
     */
    getControlList(): NullOr<HTMLElement> {
        const id = this.getControlId();
        return document.querySelector(`ui-navlist[id="${id}"]`);
    }

    /**
     * Sets class -selected to be true to highlight the item
     * @returns {UIElement}
     */
    activate(): Element {
        return this.updateElement({
            classList: { '-selected': true },
            attributes: { 'aria-current': 'true' },
        });
    }

    /**
     * Removes class -selected to be true to remove highlighting from the item
     * @returns {UIElement}
     */
    deactivate(): Element {
        return this.updateElement({
            classList: { '-selected': false },
            attributes: { 'aria-current': null },
        });
    }

    /**
     * Retrieve the href value
     * @returns {string | undefined}
     */
    getHref(): UndefinedOr<string> {
        return this.href;
    }

    /**
     * Fires callback when item's edit state is updated
     */
    onChangeEditState(): void {
        if (this.type !== UINavItem.Types.SECONDARY) {
            return;
        }
        this.rebuildChildren(
            this.buildSecondaryLinkChildren().filter(Boolean) as Array<Node>
        );
    }

    /**
     * Renders item as a primary icon
     */
    renderAsPrimaryIcon(): void {
        let canOpenMenu = false;
        const item = this.getAttribute('item');
        if (this.nav && item) {
            canOpenMenu = !!this.nav.canOpenMenu(item);
        }
        this.updateClassList({
            '-primary': true,
            '-icon': true,
        });
        this.insertElements([
            {
                tagName: 'a',
                attributes: {
                    'role': canOpenMenu ? 'menuitem' : null,
                    'title': this.label,
                    'aria-controls': canOpenMenu
                        ? this.getAttribute('controls')
                        : null,
                    'aria-expanded': canOpenMenu ? 'false' : null,
                    'href': this.href || '#',
                },
                classList: {
                    'ui-navitem__primary-link': true,
                    '-icon': true,
                },
                children: [
                    {
                        tagName: 'ui-icon',
                        attributes: {
                            glyph: this.glyph,
                            color: this.glyphColor,
                        },
                        classList: {
                            'ui-navitem__primary-icon': true,
                        },
                    },
                    {
                        tagName: 'span',
                        classList: {
                            'ui-navitem__caption': true,
                            '-visually-hidden': browser.desktopMediaMatches(),
                        },
                        children: [this.label!],
                    },
                    {
                        tagName: 'ui-icon',
                        attributes: {
                            glyph: 'redesign-chevron-right',
                            color: UIIcon.colors.DEFAULT,
                        },
                    },
                ],
            },
        ]);
    }

    /**
     * Renders item as a primary link
     */
    renderAsPrimaryLink(): void {
        let icon = this.detachChildNodes().find((node) => {
            return ['ui-icon', 'ui-icon-symbolic'].includes(node.localName);
        });

        if (!icon && this.glyph) {
            icon = this.createElement({
                tagName: 'ui-icon',
                attributes: {
                    glyph: this.glyph,
                    color: this.glyphColor,
                },
            });
        }

        this.updateClassList({ '-primary': true });

        let canOpenMenu = false;
        const item = this.getAttribute('item');
        if (this.nav && item) {
            canOpenMenu = !!this.nav.canOpenMenu(item);
        }

        const link = this.createElement({
            tagName: 'a',
            attributes: {
                'role': canOpenMenu ? 'menuitem' : null,
                'aria-controls': canOpenMenu
                    ? this.getAttribute('controls')
                    : null,
                'aria-haspopup': canOpenMenu ? 'true' : null,
                'aria-expanded': canOpenMenu ? 'false' : null,
                'data-wt-label': this.labelId ?? null,
                'href': this.href || '#',
                'id': this.getAttribute('item'),
            },
            classList: {
                'ui-navitem__primary-link': true,
            },
            children: [
                {
                    tagName: 'span',
                    classList: {
                        'ui-navitem__caption': true,
                    },
                    children: [
                        icon,
                        this.label,
                        this.labelHint && {
                            tagName: 'p',
                            classList: {
                                'ui-navitem__hint': true,
                            },
                            children: [document.createTextNode(this.labelHint)],
                        },
                    ] as ElementChildren,
                },
                {
                    tagName: 'ui-icon',
                    attributes: {
                        glyph: 'redesign-chevron-right',
                        color: UIIcon.colors.DEFAULT,
                    },
                },
            ],
        });

        this.appendChild(link);
    }

    /**
     * @returns {Array<unknown>}
     */
    buildSecondaryLinkChildren(): Array<unknown> {
        return [
            this.edit && this.buildCrossButton(),
            {
                tagName: 'a',
                attributes: {
                    'data-wt-label': this.labelId,
                    'href': this.href,
                    'id': this.getAttribute('item'),
                    'draggable': this.edit,
                },
                classList: {
                    'ui-navitem__tetriary-link': true,
                },
                children: [
                    {
                        tagName: 'span',
                        classList: {
                            'ui-navitem__caption': true,
                        },
                        children: [
                            {
                                tagName: 'span',
                                children: [
                                    document.createTextNode(this.label ?? ''),
                                ],
                            },
                            this.glyph && {
                                tagName: 'ui-icon',
                                attributes: {
                                    glyph: this.glyph,
                                    color: this.glyphColor,
                                },
                            },
                            this.badge && {
                                tagName: 'ui-badge',
                                attributes: {
                                    color: BrandColors.Lilac,
                                    layout: UIBadge.Layout.Pin,
                                },
                                children: [this.badge],
                            },
                        ],
                    },
                    this.expandable && {
                        tagName: 'ui-icon',
                        attributes: {
                            glyph: 'redesign-chevron-right',
                            color: UIIcon.colors.DEFAULT,
                        },
                    },
                ],
            },
        ];
    }

    /**
     * @fires event:remove-shortcut-item
     * @returns {HTMLElement}
     */
    buildCrossButton(): HTMLElement {
        return this.createElement({
            tagName: 'button',
            attributes: {
                'type': 'button',
                'data-event': 'remove-shortcut-item',
                'aria-label': UINavItem.labels.REMOVE_SHORTCUT,
            },
            classList: {
                'ui-navitem__remove-shortcut': true,
                '-iconed': true,
            },
            children: [
                {
                    tagName: 'ui-icon',
                    attributes: {
                        glyph: 'cross',
                        bgcolor: 'alert-red',
                    },
                },
            ],
        });
    }

    /**
     * Renders item as a secondary link
     */
    renderAsSecondaryLink(): void {
        this.updateClassList({ '-tetriary': true });
        this.insertElements(
            this.buildSecondaryLinkChildren() as Array<ElementConfig>
        );
    }

    /**
     * Renders item as a secondary link
     */
    renderAsAuxiliaryLink(): void {
        this.insertElements([
            {
                tagName: 'a',
                attributes: {
                    'data-wt-label': this.labelId!,
                    'href': this.href!,
                    'id': this.getAttribute('item'),
                    'data-badge': this.badge || null,
                },
                classList: {
                    'default-link': true,
                },
                children: [this.label!],
            },
        ]);
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        switch (name) {
            case 'edit':
                this.onChangeEditState();
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.nav = this.closest<UINav>('ui-nav');
        this.setAttribute('role', 'listitem');
        switch (this.type) {
            case UINavItem.Types.PRIMARY:
                this.renderAsPrimaryLink();
                break;
            case UINavItem.Types.SECONDARY:
                this.renderAsSecondaryLink();
                break;
            case UINavItem.Types.AUXILIARY:
                this.renderAsAuxiliaryLink();
                break;
            case UINavItem.Types.PRIMARY_ICON:
                this.renderAsPrimaryIcon();
                break;
            case UINavItem.Types.CUSTOM:
            default:
                break;
        }
    }
}
